/**
 * WalkthroughsPage — in-app companion for running the 9 canonical
 * intake walkthroughs (WT-1 through WT-9). Tracks progress, drives
 * step-by-step through each scenario, captures findings in-flow.
 *
 * Layout:
 *   Header: overall progress bar + total findings pill + export button
 *   Left  : scenario list (WT-1 .. WT-9) with status dots
 *   Right : active scenario — steps, red flags, "Report Finding" button
 *   Below : findings feed (scoped to active scenario)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, AlertCircle, Play, RotateCcw, Bug,
  Download, ChevronRight, X, Trash2,
} from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const STATUS_ICON = {
  done:        { icon: CheckCircle2, colour: 'text-emerald-600' },
  in_progress: { icon: Play,         colour: 'text-amber-600' },
  blocked:     { icon: AlertCircle,  colour: 'text-red-600' },
  null:        { icon: Circle,       colour: 'text-slate-300' },
};

const SEVERITY_COLOURS = {
  P0: 'bg-red-100 text-red-800 border-red-300',
  P1: 'bg-orange-100 text-orange-800 border-orange-300',
  P2: 'bg-amber-100 text-amber-800 border-amber-300',
  P3: 'bg-slate-100 text-slate-700 border-slate-300',
};

export default function WalkthroughsPage() {
  const [data, setData] = useState({ scenarios: [], total_scenarios: 0, completed: 0, pct: 0, total_findings: 0 });
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/walkthroughs/scenarios');
      setData(r.data);
      if (!activeId && r.data.scenarios.length) setActiveId(r.data.scenarios[0].id);
    } catch (e) {
      toast.error('Failed to load walkthroughs');
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const loadFindings = useCallback(async (wt_id) => {
    try {
      const r = await api.get('/walkthroughs/findings', { params: { wt_id } });
      setFindings(r.data.findings || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (activeId) loadFindings(activeId);
  }, [activeId, loadFindings]);

  const active = useMemo(() => data.scenarios.find(s => s.id === activeId), [data.scenarios, activeId]);

  // iter303ak-fix76 · Step-weighted progress. Under the green-ticks model
  // (Option A), current_step N means "N-1 steps completed". A scenario
  // with no session contributes 0. Done scenarios contribute their full
  // step count. This lets the top bar animate as you tab through steps
  // and gives each scenario its own mini bar in the left list.
  const stepProgress = useMemo(() => {
    let doneSteps = 0;
    let totalSteps = 0;
    const byId = {};
    for (const s of data.scenarios) {
      const total = (s.steps || []).length;
      totalSteps += total;
      let done = 0;
      if (s.session) {
        done = s.session.status === 'done'
          ? total
          : Math.max(0, Math.min(total, (s.session.current_step || 1) - 1));
      }
      doneSteps += done;
      byId[s.id] = { done, total, pct: total ? Math.round((100 * done) / total) : 0 };
    }
    return {
      byId,
      overallDone: doneSteps,
      overallTotal: totalSteps,
      overallPct: totalSteps ? Math.round((100 * doneSteps) / totalSteps) : 0,
    };
  }, [data.scenarios]);

  const startScenario = async (wt_id) => {
    try {
      await api.post(`/walkthroughs/scenarios/${wt_id}/start`);
      await load();
      toast.success(`Started ${wt_id}`);
    } catch { toast.error('Could not start scenario'); }
  };

  const advance = async (delta) => {
    if (!active?.session) return;
    const next = Math.max(1, Math.min(active.steps.length, active.session.current_step + delta));
    try {
      await api.patch(`/walkthroughs/sessions/${active.session.id}`, { current_step: next });
      await load();
    } catch { toast.error('Could not advance step'); }
  };

  // iter303ak-fix76 · Click-to-jump. Sets `current_step` to any step in
  // the scenario. Non-retroactive by design — clicking a future step
  // doesn't back-tick the ones behind it; the operator drives which
  // steps count as done via reaching them normally.
  const jumpToStep = async (stepId) => {
    if (!active?.session) return;
    if (stepId === active.session.current_step) return;
    try {
      await api.patch(`/walkthroughs/sessions/${active.session.id}`, { current_step: stepId });
      await load();
    } catch { toast.error('Could not jump to step'); }
  };

  const markStatus = async (status) => {
    if (!active?.session) return;
    try {
      await api.patch(`/walkthroughs/sessions/${active.session.id}`, { status });
      await load();
      if (status === 'done') toast.success(`${active.id} marked complete`);
    } catch { toast.error('Could not update status'); }
  };

  const resetScenario = async () => {
    if (!active?.session) return;
    if (!window.confirm(`Reset ${active.id} to step 1? Findings are kept.`)) return;
    try {
      await api.post(`/walkthroughs/sessions/${active.session.id}/reset`);
      await load();
    } catch { toast.error('Could not reset'); }
  };

  const exportMd = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/walkthroughs/findings/export`;
    const token = localStorage.getItem('access_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` }})
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'walkthrough-findings.md';
        a.click();
      });
  };

  if (loading && !data.scenarios.length) {
    return <div className="p-8 text-slate-400 text-sm">Loading walkthroughs…</div>;
  }

  const currentStep = active?.session
    ? active.steps.find(s => s.id === active.session.current_step) || active.steps[0]
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-800 font-[Manrope]">Walkthrough Runner</h1>
            <p className="text-xs text-slate-500 mt-0.5">Structured intake QA — WT-1 through WT-9</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              <b className="text-slate-800">{data.total_findings}</b> finding{data.total_findings === 1 ? '' : 's'} logged
            </span>
            <button
              data-testid="export-findings-btn"
              onClick={exportMd}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              <Download size={13} /> Export findings (.md)
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500"
              style={{ width: `${stepProgress.overallPct}%` }}
              data-testid="progress-bar"
            />
          </div>
          <span className="text-sm font-semibold text-slate-700 tabular-nums text-right whitespace-nowrap">
            <span data-testid="overall-step-pct">{stepProgress.overallPct}%</span>
            <span className="text-slate-400 font-normal ml-1.5">
              {stepProgress.overallDone} / {stepProgress.overallTotal} steps
            </span>
            <span className="text-slate-300 mx-2">·</span>
            <span className="text-slate-500 font-normal">
              {data.completed} / {data.total_scenarios} done
            </span>
          </span>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-4 p-4 max-w-[1600px] mx-auto">
        {/* Scenario list */}
        <aside className="col-span-4 lg:col-span-3 bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Scenarios</span>
          </div>
          <ul>
            {data.scenarios.map(s => {
              const status = s.session?.status || 'null';
              const { icon: Icon, colour } = STATUS_ICON[status] || STATUS_ICON.null;
              const isActive = s.id === activeId;
              const prog = stepProgress.byId[s.id] || { done: 0, total: 0, pct: 0 };
              return (
                <li key={s.id}>
                  <button
                    data-testid={`scenario-${s.id}`}
                    onClick={() => setActiveId(s.id)}
                    className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left border-b border-slate-100 hover:bg-slate-50 ${isActive ? 'bg-amber-50 border-l-2 border-l-amber-500' : ''}`}
                  >
                    <Icon size={16} className={`${colour} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-700">{s.id}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">{s.priority}</span>
                        {s.findings_count > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                            {s.findings_count} 🐛
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5 leading-tight">{s.title}</div>
                      {/* iter303ak-fix76 · per-scenario mini progress bar */}
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${status === 'done' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${prog.pct}%` }}
                            data-testid={`scenario-${s.id}-mini-progress`}
                          />
                        </div>
                        <span
                          className="text-[10px] text-slate-400 tabular-nums font-medium whitespace-nowrap"
                          data-testid={`scenario-${s.id}-pct`}
                        >
                          {prog.done}/{prog.total} · {prog.pct}%
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Active scenario */}
        <main className="col-span-8 lg:col-span-9 space-y-4">
          {active && (
            <>
              {/* Scenario header */}
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-semibold text-slate-800 font-[Manrope]">{active.id}</h2>
                      <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{active.priority}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-1">{active.title}</p>
                    <p className="text-xs text-slate-500 mt-1 italic">{active.purpose}</p>
                  </div>
                  <div className="flex gap-2">
                    {!active.session && (
                      <button
                        data-testid="start-scenario-btn"
                        onClick={() => startScenario(active.id)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <Play size={12} /> Start
                      </button>
                    )}
                    {active.session && active.session.status !== 'done' && (
                      <button
                        data-testid="mark-done-btn"
                        onClick={() => markStatus('done')}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <CheckCircle2 size={12} /> Mark scenario done
                      </button>
                    )}
                    {active.session && (
                      <button onClick={resetScenario}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
                        data-testid="reset-scenario-btn">
                        <RotateCcw size={12} /> Reset
                      </button>
                    )}
                  </div>
                </div>

                {/* Branches */}
                {active.branches && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(active.branches).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{k}</div>
                        <div className="text-xs text-slate-700 font-medium mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Steps + red flags side by side */}
              <div className="grid grid-cols-12 gap-4">
                {/* Steps */}
                <div className="col-span-12 md:col-span-8 bg-white border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                      Steps · {active.session ? `${active.session.current_step} of ${active.steps.length}` : `${active.steps.length} total`}
                    </span>
                    {active.session && (
                      <div className="flex gap-1">
                        <button onClick={() => advance(-1)} disabled={active.session.current_step <= 1}
                          className="text-xs px-2 py-1 rounded border border-slate-300 disabled:opacity-40">← Prev</button>
                        <button onClick={() => advance(1)} disabled={active.session.current_step >= active.steps.length}
                          data-testid="next-step-btn"
                          className="text-xs px-2 py-1 rounded bg-slate-800 text-white disabled:opacity-40">Next →</button>
                      </div>
                    )}
                  </div>
                  <ol className="divide-y divide-slate-100">
                    {active.steps.map(step => {
                      const currentIdx = active.session?.current_step || 0;
                      const isCurrent = step.id === currentIdx;
                      const isPast = step.id < currentIdx;
                      const clickable = !!active.session;
                      return (
                        <li key={step.id}
                          data-testid={`step-row-${step.id}`}
                          onClick={() => clickable && jumpToStep(step.id)}
                          className={`px-4 py-3 ${isCurrent ? 'bg-amber-50 border-l-4 border-l-amber-500' : ''} ${clickable ? 'cursor-pointer hover:bg-slate-50' : ''}`}>
                          <div className="flex items-start gap-3">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${isPast ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                              {isPast ? '✓' : step.id}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm ${isCurrent ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>{step.title}</div>
                              {step.detail && <div className="text-xs text-slate-500 mt-1">{step.detail}</div>}
                            </div>
                            {isCurrent && (
                              <button
                                data-testid="report-finding-btn"
                                onClick={(e) => { e.stopPropagation(); setReportOpen(true); }}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                              >
                                <Bug size={11} /> Report bug here
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>

                {/* Red flags */}
                <aside className="col-span-12 md:col-span-4 bg-white border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-4 py-2 border-b border-slate-200 bg-red-50">
                    <span className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Watch for</span>
                  </div>
                  <ul className="divide-y divide-slate-100 text-xs">
                    {(active.red_flags || []).map((rf, i) => (
                      <li key={i} className="px-4 py-2.5 text-slate-700 leading-snug">
                        <span className="text-red-500 font-bold mr-1.5">•</span>
                        {rf}
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>

              {/* Findings feed */}
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    Findings for {active.id} · {findings.length}
                  </span>
                </div>
                {findings.length === 0 ? (
                  <div className="px-4 py-6 text-xs text-slate-400 text-center">
                    No findings yet. Click "Report bug here" on the current step to log one.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {findings.map(f => (
                      <li key={f.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold border ${SEVERITY_COLOURS[f.severity] || SEVERITY_COLOURS.P2}`}>{f.severity}</span>
                              {f.kind && f.kind !== 'bug' && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded font-semibold border border-emerald-300 bg-emerald-50 text-emerald-800"
                                  data-testid={`finding-kind-badge-${f.id}`}
                                  title={f.kind}
                                >
                                  {f.kind === 'rule_discovery' && '📏 Rule'}
                                  {f.kind === 'policy_question' && '❓ Policy'}
                                  {f.kind === 'phase_2_followup' && '🔮 Phase 2'}
                                </span>
                              )}
                              <span className="text-[11px] text-slate-500">Step {f.step_id}</span>
                              {f.title && <span className="text-sm font-medium text-slate-800">{f.title}</span>}
                            </div>
                            {f.expected && <div className="text-xs mt-1"><b className="text-slate-500">Expected:</b> <span className="text-slate-700">{f.expected}</span></div>}
                            {f.actual && <div className="text-xs mt-0.5"><b className="text-slate-500">Actual:</b> <span className="text-slate-700">{f.actual}</span></div>}
                            {f.notes && <div className="text-xs mt-0.5 text-slate-600">{f.notes}</div>}
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Delete finding?')) return;
                              await api.delete(`/walkthroughs/findings/${f.id}`);
                              loadFindings(active.id); load();
                            }}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`delete-finding-${f.id}`}
                          ><Trash2 size={13} /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {reportOpen && (
        <ReportFindingModal
          scenario={active}
          stepId={active?.session?.current_step}
          onClose={() => setReportOpen(false)}
          onSaved={async () => {
            setReportOpen(false);
            await loadFindings(active.id);
            await load();
          }}
        />
      )}
    </div>
  );
}


function ReportFindingModal({ scenario, stepId, onClose, onSaved }) {
  const [severity, setSeverity] = useState('P2');
  const [kind, setKind] = useState('bug');  // Pre-UAT v10.5 · Learnings Journal
  const [title, setTitle] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // The 4 kinds, kept in lock-step with backend FINDING_KINDS.
  const KIND_OPTIONS = [
    { key: 'bug',              icon: '🐛', label: 'Bug',              hint: 'Needs fixing before go-live' },
    { key: 'rule_discovery',   icon: '📏', label: 'Rule discovery',   hint: 'Codify into RULES_AND_POLICIES.md' },
    { key: 'policy_question',  icon: '❓', label: 'Policy question',  hint: 'Needs a decision — not a bug' },
    { key: 'phase_2_followup', icon: '🔮', label: 'Phase 2 follow-up', hint: 'Redesign in the rebuild' },
  ];

  const save = async () => {
    if (!title && !notes && !actual) {
      toast.error('Add at least a title, actual behaviour, or notes');
      return;
    }
    setSaving(true);
    try {
      await api.post('/walkthroughs/findings', {
        wt_id: scenario.id,
        step_id: stepId,
        severity, kind, title, expected, actual, notes,
      });
      toast.success('Finding logged');
      onSaved();
    } catch (e) {
      toast.error('Could not save finding');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Log a finding</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{scenario?.id} · Step {stepId}</p>
          </div>
          <button onClick={onClose} data-testid="close-finding-modal"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Kind</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid="finding-kind-picker">
              {KIND_OPTIONS.map(k => (
                <button
                  key={k.key}
                  onClick={() => setKind(k.key)}
                  data-testid={`kind-${k.key}`}
                  className={`text-left px-2.5 py-1.5 text-xs rounded-md border transition ${
                    kind === k.key
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <div className="font-semibold flex items-center gap-1.5">
                    <span>{k.icon}</span>{k.label}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{k.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Severity</label>
            <div className="flex gap-1.5 mt-1">
              {['P0','P1','P2','P3'].map(p => (
                <button key={p} onClick={() => setSeverity(p)} data-testid={`severity-${p}`}
                  className={`px-3 py-1 text-xs rounded-md border ${severity === p ? SEVERITY_COLOURS[p] + ' font-bold' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Short title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} data-testid="finding-title"
              placeholder="e.g. Postcode didn't auto-fill"
              className="w-full mt-1 text-sm px-2.5 py-1.5 border border-slate-300 rounded"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Expected</label>
            <input value={expected} onChange={e => setExpected(e.target.value)} data-testid="finding-expected"
              placeholder="What should have happened"
              className="w-full mt-1 text-sm px-2.5 py-1.5 border border-slate-300 rounded"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Actual</label>
            <input value={actual} onChange={e => setActual(e.target.value)} data-testid="finding-actual"
              placeholder="What actually happened"
              className="w-full mt-1 text-sm px-2.5 py-1.5 border border-slate-300 rounded"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} data-testid="finding-notes"
              placeholder="Any extra context — repro steps, side effects, hypothesis"
              className="w-full mt-1 text-sm px-2.5 py-1.5 border border-slate-300 rounded"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-600 hover:bg-white">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-finding-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <Bug size={12} /> {saving ? 'Saving…' : 'Save finding'}
          </button>
        </div>
      </div>
    </div>
  );
}
